import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  tag: string;
  children: React.ReactNode;
}

const allArticles = [
  { title: 'Ultimate Guide 2026', path: '/basetao-spreadsheet-ultimate-guide' },
  { title: 'Basetao Review 2026', path: '/basetao-review-2026' },
  { title: 'Is Basetao Legit?', path: '/is-basetao-legit' },
  { title: 'Shipping Cost Guide', path: '/basetao-shipping-cost' },
  { title: 'Warehouse Guide', path: '/basetao-warehouse-guide' },
  { title: 'Coupon & Discounts', path: '/basetao-coupon-discount' },
  { title: 'Best Product Finds', path: '/basetao-product-finds' },
  { title: 'Vs Competitors', path: '/basetao-vs-competitors' },
  { title: 'Beginner Guide', path: '/basetao-beginner-guide' },
  { title: 'Safety Tips', path: '/basetao-safety-tips' },
];

const articleImages: Record<string, string> = {
  '/basetao-spreadsheet-ultimate-guide': 'https://readdy.ai/api/search-image?query=Organized%20desk%20with%20laptop%20displaying%20spreadsheet%20data%2C%20warm%20natural%20lighting%2C%20minimalist%20workspace%2C%20cream%20and%20beige%20tones%2C%20editorial%20productivity%20scene%2C%20clean%20composition&width=800&height=500&seq=article-img-guide&orientation=landscape',
  '/basetao-review-2026': 'https://readdy.ai/api/search-image?query=Person%20unboxing%20fashion%20package%20on%20wooden%20table%2C%20warm%20natural%20light%2C%20cream%20background%2C%20editorial%20lifestyle%20photography%2C%20clean%20minimalist%20aesthetic&width=800&height=500&seq=article-img-review&orientation=landscape',
  '/is-basetao-legit': 'https://readdy.ai/api/search-image?query=Secure%20online%20shopping%20concept%20with%20credit%20card%20and%20lock%20icon%20on%20warm%20cream%20desk%2C%20soft%20natural%20lighting%2C%20minimalist%20composition%2C%20editorial%20style&width=800&height=500&seq=article-img-legit&orientation=landscape',
  '/basetao-shipping-cost': 'https://readdy.ai/api/search-image?query=International%20shipping%20boxes%20and%20parcels%20on%20minimalist%20cream%20surface%2C%20warm%20ambient%20lighting%2C%20clean%20logistics%20photography%2C%20editorial%20composition&width=800&height=500&seq=article-img-shipping&orientation=landscape',
  '/basetao-warehouse-guide': 'https://readdy.ai/api/search-image?query=Organized%20warehouse%20shelves%20with%20packages%20in%20warm%20minimalist%20setting%2C%20clean%20cream%20backdrop%2C%20soft%20lighting%2C%20editorial%20industrial%20photography&width=800&height=500&seq=article-img-warehouse&orientation=landscape',
  '/basetao-coupon-discount': 'https://readdy.ai/api/search-image?query=Shopping%20discount%20concept%20with%20fashion%20items%20and%20price%20tags%20on%20warm%20cream%20surface%2C%20minimalist%20composition%2C%20soft%20natural%20lighting%2C%20editorial%20style&width=800&height=500&seq=article-img-coupon&orientation=landscape',
  '/basetao-product-finds': 'https://readdy.ai/api/search-image?query=Beautiful%20fashion%20product%20flatlay%20with%20sneakers%20hoodie%20and%20accessories%20on%20cream%20marble%2C%20warm%20natural%20light%2C%20editorial%20photography%2C%20clean%20minimalist%20composition&width=800&height=500&seq=article-img-finds&orientation=landscape',
  '/basetao-vs-competitors': 'https://readdy.ai/api/search-image?query=Comparison%20chart%20concept%20with%20shopping%20icons%20on%20warm%20cream%20desk%2C%20minimalist%20workspace%2C%20soft%20natural%20lighting%2C%20clean%20editorial%20composition&width=800&height=500&seq=article-img-compare&orientation=landscape',
  '/basetao-beginner-guide': 'https://readdy.ai/api/search-image?query=Person%20laptop%20shopping%20online%20on%20warm%20cream%20desk%2C%20minimalist%20workspace%2C%20soft%20natural%20lighting%2C%20editorial%20lifestyle%20photography%2C%20clean%20composition&width=800&height=500&seq=article-img-beginner&orientation=landscape',
  '/basetao-safety-tips': 'https://readdy.ai/api/search-image?query=Secure%20shopping%20concept%20with%20shield%20and%20package%20on%20warm%20cream%20surface%2C%20minimalist%20composition%2C%20soft%20natural%20lighting%2C%20clean%20editorial%20style&width=800&height=500&seq=article-img-safety&orientation=landscape',
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.link';
const faviconUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png';

export default function ArticleLayout({ title, subtitle, tag, children }: ArticleLayoutProps) {
  const [guideOpen, setGuideOpen] = useState(false);
  const location = useLocation();
  const articleUrl = `${siteUrl}${location.pathname}`;
  const articleImage = articleImages[location.pathname] || faviconUrl;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: subtitle,
    image: articleImage,
    datePublished: '2026-08-10',
    dateModified: '2026-08-10',
    author: {
      '@type': 'Organization',
      name: 'BasetaoSheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'BasetaoSheet',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: faviconUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'en',
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <JsonLd data={articleSchema as unknown as Record<string, unknown>} />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-heading text-xl font-bold text-foreground-900 tracking-tight">
                Basetao<span className="text-primary-500">Sheet</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-5">
              <Link to="/" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                Home
              </Link>

              <div className="relative" onMouseEnter={() => setGuideOpen(true)} onMouseLeave={() => setGuideOpen(false)}>
                <button className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                  Shopping Guide
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>
                {guideOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 z-50">
                    {allArticles.map((a) => (
                      <Link
                        key={a.path}
                        to={a.path}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {a.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Visit Main Site
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
            </div>

            <Link
              to="/"
              className="md:hidden inline-flex items-center gap-1 text-sm font-semibold text-primary-600 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </div>
              Home
            </Link>
          </div>
        </div>
      </nav>

      <main>
        <article className="w-full bg-background-50">
          <header className="w-full bg-background-100 border-b border-background-200/70 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-6 md:px-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-medium text-primary-600 bg-primary-100/70 px-2.5 py-1 rounded-full whitespace-nowrap">{tag}</span>
                <span className="text-xs text-foreground-500">Updated August 2026</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-5 leading-tight">
                {title}
              </h1>
              <p className="text-sm md:text-lg text-foreground-600 leading-relaxed">
                {subtitle}
              </p>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16">
            {children}
          </div>
        </article>
      </main>
    </>
  );
}